@switch (variant()) {
  <!-- R-W1 .. R-W3, R-W6: the "Next payment" item of the general-info band -->
  @case ('band') {
    @if (listDownHere()) {
      <div class="px-3 border-l border-l-kz-bluegrey-400 pp-np" data-testid="res-next-payment">
        <span class="text-kz-text-secondary" [attr.data-testid]="'res-next-payment-unavailable-' + rfid()">{{
          'Next payment: not available' | translate
        }}</span>
      </div>
    } @else if (retrying()) {
      <div class="px-3 border-l border-l-kz-bluegrey-400 pp-np" data-testid="res-next-payment">
        <span role="status" data-testid="res-payment-loading"
          ><span class="font-bold">{{ 'Loading the payment schedule…' | translate }}</span>
          <span class="pp-skel" aria-hidden="true"></span
        ></span>
      </div>
    } @else if (plan().state === 'unavailable') {
      <div class="px-3 border-l border-l-kz-bluegrey-400 pp-np" data-testid="res-next-payment">
        <div role="alert" data-testid="res-payment-unavailable">
          <span class="pp-sub font-bold text-kz-danger" data-testid="res-payment-error-message">{{
            errorMessage() | translate
          }}</span>
          <span class="text-kz-danger"
            ><span class="font-bold" aria-hidden="true">! </span>{{ 'Next payment: not available' | translate }}</span
          >
          <span class="pp-sub text-kz-text-secondary">{{ 'Error code' | translate }}: {{ plan().errorCode }}</span>
          @if (traceId(); as id) {
            <span class="pp-sub"
              ><span class="font-bold">{{ 'Trace ID' | translate }}: </span
              ><span data-testid="res-payment-trace-id">{{ id }}</span></span
            >
          }
          <span class="pp-sub">
            @if (traceId(); as id) {
              <button
                type="button"
                class="link-btn"
                data-testid="res-payment-trace-copy"
                [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
                (click)="copy(id)"
              >
                {{ (copied() ? 'Copied' : 'Copy') | translate }}
              </button>
            }
            <button type="button" class="link-btn" data-testid="res-payment-retry" (click)="retry()">
              {{ 'Try again' | translate }}
            </button>
          </span>
        </div>
      </div>
    } @else if (next(); as next) {
      <div class="px-3 border-l border-l-kz-bluegrey-400 pp-np" data-testid="res-next-payment">
        @switch (next.kind) {
          @case ('closed') {
            <span class="text-kz-grey-400" data-testid="res-payment-schedule-closed">{{
              'Payment schedule closed: the reservation is cancelled' | translate
            }}</span>
          }
          @case ('paid') {
            <span class="text-kz-success" data-testid="res-paid-in-full"
              ><span aria-hidden="true">✓ </span>{{ 'Paid in full' | translate }}</span
            >
          }
          @case ('overdue') {
            <span class="font-bold">{{ 'Payment overdue' | translate }}: </span>
            <span class="text-kz-danger" role="status" data-testid="res-payment-overdue-text"
              ><span aria-hidden="true">! </span>{{ '{a} ({p}) since {d}' | translate: nextParams() }}</span
            >
          }
          @default {
            <span class="font-bold">{{ 'Next payment' | translate }}: </span>
            <span data-testid="res-next-payment-text">{{ '{a} ({p}) by {d}' | translate: nextParams() }}</span>
          }
        }
      </div>
    }
  }

  <!-- R-W5: the Payment schedule tab - a table from 820 px, one card per step below -->
  @case ('schedule') {
    @if (retrying()) {
      <div class="pp-tabnote text-xs" role="status" data-testid="res-payment-schedule-loading">
        <span class="font-bold">{{ 'Loading the payment schedule…' | translate }}</span>
        <span class="pp-skel" aria-hidden="true"></span>
      </div>
    } @else if (plan().state === 'unavailable') {
      <div class="pp-tabnote text-xs text-kz-danger" role="alert" data-testid="res-payment-schedule-unavailable">
        <span class="pp-sub font-bold" data-testid="res-payment-schedule-error-message">{{
          errorMessage() | translate
        }}</span>
        <span class="font-bold" aria-hidden="true">! </span
        >{{
          'The payment schedule is not available right now. The rest of the reservation is shown as usual.' | translate
        }}
        {{ 'Error code' | translate }}: {{ plan().errorCode }}
        @if (traceId(); as id) {
          <span class="pp-sub"
            ><span class="font-bold">{{ 'Trace ID' | translate }}: </span
            ><span data-testid="res-payment-schedule-trace-id">{{ id }}</span></span
          >
        }
        <span class="pp-sub">
          @if (traceId(); as id) {
            <button
              type="button"
              class="link-btn"
              data-testid="res-payment-schedule-trace-copy"
              [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
              (click)="copy(id)"
            >
              {{ (copied() ? 'Copied' : 'Copy') | translate }}
            </button>
          }
          <button type="button" class="link-btn" data-testid="res-payment-schedule-retry" (click)="retry()">
            {{ 'Try again' | translate }}
          </button>
        </span>
      </div>
    } @else if (!steps().length) {
      <div class="pp-tabnote text-xs text-kz-text-secondary" data-testid="res-payment-schedule-none">—</div>
    } @else {
      <div class="w-full font-normal text-xs overflow-y-hidden" [attr.data-testid]="'res-payment-schedule-' + rfid()">
        <div class="hidden st:grid bg-kz-primary pp-cols py-4 font-normal text-white">
          <div class="px-2">{{ 'Step' | translate }}</div>
          <div class="px-2 border-l">{{ 'Due date' | translate }}</div>
          <div class="px-2 border-l pp-num">{{ 'Share' | translate }}</div>
          <div class="px-2 border-l pp-num">{{ 'Amount' | translate }}</div>
          <div class="px-2 border-l pp-num">{{ 'Paid' | translate }}</div>
          <div class="px-2 border-l pp-num">{{ 'To pay' | translate }}</div>
          <div class="px-2 border-l">{{ 'Status' | translate }}</div>
        </div>
        @for (step of steps(); track step.stepNo) {
          <div
            class="hidden st:grid pp-cols py-5 pp-row"
            [class.pp-late]="step.isLate"
            [class.pp-next]="step.isNext"
            [class.bg-c-ground]="!step.isLate && !step.isNext"
            [attr.data-testid]="'res-payment-schedule-row-' + rfid() + '-' + step.stepNo"
          >
            <div class="px-2" [class.font-bold]="step.isNext">
              {{ 'Step {n}' | translate: { n: step.stepNo } }}
              @if (step.isNext) {
                ({{ 'next' | translate }})
              }
            </div>
            <div class="px-2">{{ step.due }}</div>
            <div class="px-2 pp-num">{{ step.percent }}</div>
            <div class="px-2 pp-num">{{ step.amount }}</div>
            <div class="px-2 pp-num">{{ step.paid }}</div>
            <div
              class="px-2 pp-num"
              [class.text-kz-danger]="step.isLate || step.isNegative"
              [class.font-bold]="step.isLate"
            >
              {{ step.outstanding }}
            </div>
            <div class="px-2">
              <ng-container *ngTemplateOutlet="statusRef; context: { $implicit: step }" />
            </div>
          </div>
        }
        <div class="st:hidden flex flex-col gap-3 py-3">
          @for (step of steps(); track step.stepNo) {
            <div
              class="py-4 flex flex-col gap-2 px-2 border-l-4 rounded overflow-hidden"
              [class.bg-kz-red-50]="step.isLate"
              [class.border-kz-danger]="step.isLate"
              [class.bg-kz-light-blue-50]="step.isNext"
              [class.border-kz-primary]="step.isNext"
              [class.bg-kz-layout-featured]="!step.isLate && !step.isNext"
              [class.border-kz-bluegrey]="!step.isLate && !step.isNext"
              [attr.data-testid]="'res-payment-schedule-card-' + rfid() + '-' + step.stepNo"
            >
              <div class="flex flex-row justify-between gap-2">
                <span class="font-bold"
                  >{{ 'Step {n}' | translate: { n: step.stepNo } }}
                  @if (step.isNext) {
                    ({{ 'next' | translate }})
                  }
                </span>
                <ng-container *ngTemplateOutlet="statusRef; context: { $implicit: step }" />
              </div>
              <div class="flex xs:flex-col sm:flex-row gap-2">
                <span class="font-bold">{{ 'Due date' | translate }}:</span> <span>{{ step.due }}</span>
              </div>
              <div class="flex xs:flex-col sm:flex-row gap-2">
                <span class="font-bold">{{ 'Share' | translate }}:</span> <span>{{ step.percent }}</span>
              </div>
              <div class="flex xs:flex-col sm:flex-row gap-2">
                <span class="font-bold">{{ 'Amount' | translate }}:</span> <span>{{ step.amountWithCurrency }}</span>
              </div>
              <div class="flex xs:flex-col sm:flex-row gap-2">
                <span class="font-bold">{{ 'Paid' | translate }}:</span> <span>{{ step.paidWithCurrency }}</span>
              </div>
              <div class="flex xs:flex-col sm:flex-row gap-2">
                <span class="font-bold">{{ 'To pay' | translate }}:</span>
                <span [class.text-kz-danger]="step.isLate || step.isNegative" [class.font-bold]="step.isLate">{{
                  step.outstandingWithCurrency
                }}</span>
              </div>
            </div>
          }
        </div>
        <div class="bg-kz-light-blue-50 px-2 py-4 flex flex-row flex-wrap gap-3">
          <div class="px-3">
            <span class="font-bold">{{ 'Payment schedule' | translate }}: </span>
            <span [attr.data-testid]="'res-payment-schedule-summary-' + rfid()">{{
              '{n} steps, total {t}' | translate: summaryParams()
            }}</span>
          </div>
          @if (closed()) {
            <div class="px-3 border-l border-l-kz-bluegrey-400 text-kz-grey-400">
              {{ 'Payment schedule closed: the reservation is cancelled' | translate }}
            </div>
          }
        </div>
      </div>
    }
  }

  <!-- R-W4: the Prices dialog - the next payment under the Commission rows, then the steps -->
  @case ('prices') {
    @if (listDown() || plan().state === 'unavailable') {
      <div
        class="flex flex-row p-2 px-4 text-kz-text-secondary"
        data-testid="reservation-price-next-payment-unavailable"
      >
        <div class="flex-1">{{ 'Next payment: not available' | translate }}</div>
      </div>
    } @else if (steps().length) {
      <div
        class="flex flex-row p-2 px-4"
        data-testid="reservation-price-next-payment"
        [class.text-kz-danger]="isLate()"
      >
        @if (closed()) {
          <div class="flex-1 text-kz-grey-400">
            {{ 'Payment schedule closed: the reservation is cancelled' | translate }}
          </div>
        } @else if (next()?.kind === 'paid') {
          <div class="flex-1 text-kz-success"><span aria-hidden="true">✓ </span>{{ 'Paid in full' | translate }}</div>
        } @else if (next(); as next) {
          <!-- Q-269 (docs#620): share and date in the label, the amount with its currency in the amount column, the invoice column empty -->
          <div class="flex-1" data-testid="reservation-price-next-payment-label">
            @if (isLate()) {
              <span aria-hidden="true">! </span>{{ 'Payment overdue' | translate }}
            } @else {
              {{ 'Next payment' | translate }}
            }
            {{ (isLate() ? '({p}) since {d}' : '({p}) by {d}') | translate: nextParams() }}
          </div>
          <div class="flex-1 text-right" data-testid="reservation-price-next-payment-amount">
            @if (nextCurrency(); as currency) {
              {{ next.amount | priceFormat: currency : currency }}
            } @else {
              <!-- no currency known: the plain amount, never converted (D1) -->
              {{ planMoney(next.amount) }}
            }
          </div>
          <div class="flex-1 text-right"></div>
        }
      </div>
      <div class="bg-kz-light-blue-50" data-testid="reservation-price-schedule">
        @for (step of steps(); track step.stepNo) {
          <div class="flex flex-row p-2 px-4" [attr.data-testid]="'reservation-price-schedule-step-' + step.stepNo">
            <div class="flex-1">{{ 'Step {n}' | translate: { n: step.stepNo } }} · {{ step.due }}</div>
            <div class="flex-1 text-right">{{ step.amount }}</div>
            <div class="flex-1 text-right">
              <ng-container *ngTemplateOutlet="statusRef; context: { $implicit: step }" />
            </div>
          </div>
        }
      </div>
    }
  }

  <!-- R-W7 / R-W8 (C17): payments cannot be read - one coded box above the list (or instead of it for Overdued) -->
  @case ('list') {
    @if (listState(); as state) {
      @if (state.kind !== 'ok') {
        <div class="pp-alert text-sm" role="alert" [attr.data-testid]="state.prefix + '-unavailable'">
          <span class="pp-sub font-bold text-kz-danger" [attr.data-testid]="state.prefix + '-error-message'">{{
            listErrorMessage() | translate
          }}</span>
          <span class="text-kz-danger"
            ><span class="font-bold" aria-hidden="true">! </span>{{ listText() | translate }}</span
          >
          <span class="pp-sub text-kz-text-secondary"
            >{{ 'Error code' | translate }}:
            <span [attr.data-testid]="state.prefix + '-error-code'">{{ state.errorCode }}</span></span
          >
          @if (listTraceId(); as id) {
            <span class="pp-sub"
              ><span class="font-bold">{{ 'Trace ID' | translate }}: </span
              ><span [attr.data-testid]="state.prefix + '-trace-id'">{{ id }}</span></span
            >
          }
          <span class="pp-sub">
            @if (listTraceId(); as id) {
              <button
                type="button"
                class="link-btn"
                [attr.data-testid]="state.prefix + '-trace-copy'"
                [attr.aria-label]="'Copy Trace ID to clipboard' | translate"
                (click)="copy(id)"
              >
                {{ (copied() ? 'Copied' : 'Copy') | translate }}
              </button>
            }
            <button
              type="button"
              class="link-btn"
              [attr.data-testid]="state.prefix + '-retry'"
              [disabled]="listRetrying()"
              (click)="retryList()"
            >
              {{ 'Try again' | translate }}
            </button>
            @if (listRetrying()) {
              <span role="status" [attr.data-testid]="state.prefix + '-retrying'">{{
                'Trying again…' | translate
              }}</span>
            }
          </span>
        </div>
      }
    }
  }
}

<ng-template #statusRef let-step>
  <span [class]="step.statusClass" [attr.data-testid]="'res-payment-step-status-' + rfid() + '-' + step.stepNo">
    <!-- "!" then the label: the line break below collapses to the one space between them -->
    @if (step.isLate) {
      <span aria-hidden="true">!</span>
    }
    {{ step.statusKey | translate: step.statusParams }}</span
  >
</ng-template>
